import { useEffect } from "react";

/**
 * Set document.title and named meta tags on mount.
 * Restores previous values on unmount. No dependency needed since options
 * is captured on mount.
 */
export function useSeo({ title, description, robots, canonical } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (attr, name, content) => {
      if (!content) return () => {};
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      let created = false;
      const prev = el?.getAttribute("content");
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
        created = true;
      }
      el.setAttribute("content", content);
      return () => {
        if (created) el.remove();
        else if (prev !== undefined) el.setAttribute("content", prev);
      };
    };

    const setLink = (rel, href) => {
      if (!href) return () => {};
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      let created = false;
      const prev = el?.getAttribute("href");
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
        created = true;
      }
      el.setAttribute("href", href);
      return () => {
        if (created) el.remove();
        else if (prev !== undefined) el.setAttribute("href", prev);
      };
    };

    const cleanups = [
      setMeta("name", "description", description),
      setMeta("name", "robots", robots),
      setLink("canonical", canonical),
    ];

    return () => {
      document.title = prevTitle;
      cleanups.forEach((fn) => fn && fn());
    };
  }, [title, description, robots, canonical]);
}
